import React from "react";
import { FileEntity, Space, Track } from "@highnote/server/src/core/entities";
import { PREVIEW_ICON_SIZE, PreviewIcon } from "App/common/PreviewIcon";
import { ReactComponent as DefaultUploadFileSVG } from "App/common/icons-v2/default-upload-file.svg";
import { ReactComponent as ExternalLinkSVG } from "App/common/icons-v2/external-link.svg";
import { ReactComponent as FileReduceFillSVG } from "App/common/icons-v2/file-reduce-fill.svg";
import { ReactComponent as FolderFillSVG } from "App/common/icons-v2/folder-fill.svg";
import { ReactComponent as MusicNoteSVG } from "App/common/icons-v2/music-fill.svg";
import { ReactComponent as TrackVersionSVG } from "App/common/icons-v2/waveform.svg";
import {
  ENTITY_TYPE,
  EntityRowConfig,
  Version,
} from "App/components/EntityTable/config";
import { formatFileSize } from "App/modules/utils";
import { getFallbackArtwork } from "../EntityTable/EntityPreview";
import { DragData } from "./types";

import styles from "./DnDDragPreviewRow.module.scss";

export const DnDDragPreviewRow = ({
  data,
  entityData,
  selectedEntities,
}: {
  data: DragData;
  entityData?: EntityRowConfig;
  selectedEntities?: Record<string, unknown>;
}) => {
  const isSelected = !!selectedEntities?.[data.id];
  const selectedCount = Object.keys(selectedEntities || {}).length;

  // If multiple items are selected, show count message
  if (isSelected && selectedCount > 1) {
    return (
      <div className={styles.dragPreview}>{selectedCount} items moving</div>
    );
  }

  // If we have entity data, show EntityPreview-like drag preview
  if (entityData) {
    const { title, description } = getTitleAndDescription(entityData);
    const artworkUrl = getArtworkUrl(entityData);
    const fallback = getFallbackArtwork(entityData);

    return (
      <div className={styles.dragPreview}>
        <div className={styles.dragPreviewThumbnail}>
          <PreviewIcon
            src={artworkUrl}
            size={PREVIEW_ICON_SIZE.LARGE}
            fallback={fallback}
          />
        </div>

        <div className={styles.dragPreviewContent}>
          <div className={styles.dragPreviewTitle}>{title}</div>
          <div className={styles.dragPreviewDescription}>{description}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dragPreview}>
      {data.name || data.id || "1 item moving"}
    </div>
  );
};

// Get artwork URL and fallback using the same logic as EntityPreview
const getArtworkUrl = (entityData: EntityRowConfig) => {
  const entity = entityData.entity as Record<string, unknown>;
  if (entityData.type === ENTITY_TYPE.SPACE && entity?.artworkUrl) {
    return entity.artworkUrl as string;
  }
  if (entityData.type === ENTITY_TYPE.TRACK && entity?.artworkUrl) {
    return entity.artworkUrl as string;
  }
  return undefined;
};

// Generate title and description with icons using the same logic as EntityPreview
const getTitleAndDescription = (entityData: EntityRowConfig) => {
  const entity = entityData.entity;

  if (entityData.type === ENTITY_TYPE.SPACE) {
    return {
      title: ((entity as Space)?.name as string) || "Untitled Space",
      description: (
        <div className={styles.dragPreviewDescriptionContent}>
          <FolderFillSVG className={styles.dragPreviewIcon} />
          <span>Space</span>
        </div>
      ),
    };
  }

  if (entityData.type === ENTITY_TYPE.TRACK) {
    const artistName =
      ((entity as Track)?.artistName as string) ||
      ((entity as Track)?.createdByName as string) ||
      "";
    return {
      title: ((entity as Track)?.title as string) || "Untitled Track",
      description: (
        <div className={styles.dragPreviewDescriptionContent}>
          <MusicNoteSVG className={styles.dragPreviewIcon} />
          {artistName && <span>{artistName}</span>}
        </div>
      ),
    };
  }

  if (entityData.type === ENTITY_TYPE.TRACK_VERSION) {
    return {
      title: ((entity as Version)?.name as string) || "Untitled Version",
      description: (
        <div className={styles.dragPreviewDescriptionContent}>
          <TrackVersionSVG className={styles.dragPreviewIcon} />
          <span>Track Version</span>
        </div>
      ),
    };
  }

  if (entityData.type === ENTITY_TYPE.FILE) {
    const file = entity as FileEntity;
    const fileType = (file?.fileType as string) || "File";
    const fileSize = file?.size ? formatFileSize(file.size as number) : null;

    let icon = <FileReduceFillSVG className={styles.dragPreviewIcon} />;
    if (file?.url) {
      icon = <ExternalLinkSVG className={styles.dragPreviewIcon} />;
    }

    return {
      title:
        (file?.name as string) || (file?.fileName as string) || "Untitled File",
      description: (
        <div className={styles.dragPreviewDescriptionContent}>
          {icon}
          <span>{[fileType, fileSize].filter(Boolean).join(" • ")}</span>
        </div>
      ),
    };
  }

  return {
    title: entityData.name || entityData.id || "Untitled",
    description: (
      <div className={styles.dragPreviewDescriptionContent}>
        <DefaultUploadFileSVG className={styles.dragPreviewIcon} />
        <span>{entityData.type.replace("_", " ").toLowerCase()}</span>
      </div>
    ),
  };
};
